import {
  alpha,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
} from "@mui/material";
import Chip from "@mui/material/Chip";
import React, { FC, useCallback, useEffect } from "react";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { ConfirmDialogEnum, ConfirmDialogType, IUser } from "../../utils/types";
import { useRegisterUserMutation, useUpdateUserMutation } from "../../api/apiSlice";
import toast from "react-hot-toast";
import { RoleValue } from "../../api/api.types";
import { ConfirmDialog } from "../../components/Dialogs/ConfirmDialog";
import { validatePassword, getApiErrorMessage } from "../../utils";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import { useDetectChanges } from "../../hooks";

interface CreateOrEditUserProps {
  user?: IUser | null;
  handleClose: () => void;
}

const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

// Define the roles available for assignment from the frontend.
// Note: "Mobile User" role is excluded because assigning this role is not allowed from the frontend.
// TODO: If appropriate, the backend API should be implemented to return the available roles dynamically.
const roles: RoleValue[] = ["Admin", "Approver", "Creator", "Marketing", "Translator"];

export const CreateOrEditUser: FC<CreateOrEditUserProps> = props => {
  const { user, handleClose } = props;
  const isEditing = !!user;

  const [userDetails, setUserDetails] = React.useState<Partial<IUser> | null>(user || null);
  const [initialUserDetails, setInitialUserDetails] = React.useState<Partial<IUser> | null>(null);
  const defaultRoles = userDetails?.roles?.map(role => role.role) || [];
  const [selectedRoles, setSelectedRoles] = React.useState<string[]>(defaultRoles);
  const [password, setPassword] = React.useState<string>("");
  const [openedConfirmDialog, setOpenedConfirmDialog] = React.useState<ConfirmDialogType | undefined>();
  const [passwordError, setPasswordError] = React.useState<string | null>(null);
  const [roleError, setRoleError] = React.useState<string | null>(null);
  const [emailError, setEmailError] = React.useState<string | null>(null);
  const [nameError, setNameError] = React.useState<string | null>(null);
  const [showPassword, setShowPassword] = React.useState<boolean>(false);
  const [disableSaveButton, setDisableSaveButton] = React.useState<boolean>(true);
  const [isCustomCreateLoading, setIsCustomCreateLoading] = React.useState<boolean>(false);

  const handleOnChangeRoles = (_event: React.SyntheticEvent<Element, Event>, values: any) => {
    setSelectedRoles(values);

    if (values.length === 0) {
      setRoleError("At least one role should be selected");
    } else {
      setRoleError(null);
    }
  };

  const handleOnPasswordChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPassword(event.target.value);
    const { isValid, message } = validatePassword(event.target.value);
    if (!isValid && event.target.value) {
      setPasswordError(message);
    } else {
      setPasswordError(null);
    }
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setUserDetails({ ...userDetails, [event.target.id]: event.target.value });
    if (event.target.id === "email") {
      if (!emailRegex.test(event.target.value)) {
        setEmailError("Invalid email address");
      } else {
        setEmailError(null);
      }
    }

    if (event.target.id === "name") {
      if (!event.target.value) {
        setNameError("Name is required");
      } else {
        setNameError(null);
      }
    }
  };

  const [updateUser, updateUserResult] = useUpdateUserMutation();
  const [registerUser] = useRegisterUserMutation();
  const { isSuccess: isEditSuccess, isLoading: isUpdatingLoading } = updateUserResult;
  const isLoading = isEditing ? isUpdatingLoading : isCustomCreateLoading;

  const handleUserCreation = async () => {
    try {
      setIsCustomCreateLoading(true);

      const data = await registerUser({
        name: userDetails?.name || "",
        email: userDetails?.email || "",
        password: password,
      }).unwrap();

      await updateUser({
        id: data.id,
        roles: selectedRoles as RoleValue[],
      }).unwrap();

      toast.success("User has been successfully created");
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, "Failed to create user");
      toast.error(errorMessage);
    } finally {
      setIsCustomCreateLoading(false);
    }
  };

  const handleSubmit = () => {
    if (userDetails?.id && isEditing) {
      // Only send email if it has changed
      const updatedFields: Partial<IUser> = {};
      if (userDetails?.email?.toLowerCase() !== user.email.toLowerCase()) {
        updatedFields.email = userDetails.email;
      }
      updateUser({
        id: userDetails?.id,
        ...updatedFields,
        name: userDetails?.name,
        password: password,
        roles: selectedRoles as RoleValue[],
      })
        .unwrap()
        .then(() => {
          toast.success("User has been successfully updated");
        })
        .catch(error => {
          const errorMessage = getApiErrorMessage(error, "Failed to update user");
          toast.error(errorMessage);
        });
      return;
    }

    if (!isEditing) {
      handleUserCreation();
    }
  };

  const onClickSubmitButton = () => {
    if (isEditing) {
      setOpenedConfirmDialog(ConfirmDialogEnum.Save);
      return;
    }
    handleSubmit();
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const hasDetectChange = useDetectChanges<Partial<IUser>>({
    initialObject: initialUserDetails,
    object: userDetails,
    ignoreKeys: ["roles"],
  });

  const hasChanges = useCallback(() => {
    const userRoles = userDetails?.roles?.map(role => role.role) || [];
    const rolesChanged = userRoles.sort().join(",") !== selectedRoles.sort().join(",");
    return hasDetectChange() || rolesChanged || password !== "";
  }, [hasDetectChange, password, selectedRoles, userDetails?.roles]);

  useEffect(() => {
    // If Creating, password is required but if editing, reset password is optional
    const hasPassword = isEditing ? true : !!password;
    const hasError = !!emailError || !!nameError || !!passwordError || !!roleError;
    const hasRequiredFields = !!selectedRoles.length && !!userDetails?.email && !!userDetails?.name && hasPassword;
    const shouldDisable = !hasChanges() || isLoading || hasError || !hasRequiredFields;
    setDisableSaveButton(shouldDisable);
  }, [
    emailError,
    hasChanges,
    isEditing,
    isLoading,
    nameError,
    password,
    passwordError,
    roleError,
    selectedRoles.length,
    userDetails?.email,
    userDetails?.name,
  ]);

  useEffect(() => {
    // Close the dialog only upon a successful edit only.
    // This is because after creating a user, we need to update the user to add roles selected while creating.
    // Therefore, the dialog should remain open until the roles are successfully added.
    if (isEditSuccess) {
      handleClose();
    }
  }, [handleClose, isEditSuccess]);

  // Set initial user details on component mount
  useEffect(() => {
    if (user) {
      setInitialUserDetails(user);
    }
  }, [user]);

  // Editing the email address for a user with the "Mobile User" role is not allowed.
  // Check if the selected roles include "Mobile User" and disable email editing if the user is being edited.
  const hasMobileRole = selectedRoles.includes("Mobile User");
  const disableEmailEdit = isEditing && hasMobileRole;

  return (
    <Box display="flex" flexDirection="column" justifyContent="space-between">
      {openedConfirmDialog === ConfirmDialogEnum.Save && (
        <ConfirmDialog
          open={true}
          title="Confrim"
          body={`Are you sure you want to update this User: ${userDetails?.name}?`}
          onClose={() => setOpenedConfirmDialog(undefined)}
          onConfirm={() => handleSubmit()}
          loading={isUpdatingLoading}
        />
      )}
      <DialogTitle>{isEditing ? `Edit User ${user.name}` : "Create User"}</DialogTitle>
      <DialogContent>
        <Box gap={1} display="flex" flexDirection="column">
          <TextField
            id="name"
            label="Name"
            value={userDetails?.name || ""}
            onChange={handleOnChange}
            onBlur={handleOnChange}
            error={!!nameError}
            helperText={nameError || `${userDetails?.name?.length || 0}/50`}
            slotProps={{
              htmlInput: { maxLength: 50 },
              formHelperText: { sx: { textAlign: nameError ? "left" : "right" } },
            }}
            fullWidth
            required
            margin="normal"
          />
          <TextField
            id="email"
            label="Email"
            value={userDetails?.email || ""}
            onChange={handleOnChange}
            disabled={disableEmailEdit}
            onBlur={handleOnChange}
            error={!!emailError}
            helperText={emailError}
            fullWidth
            required
            margin="normal"
          />

          <TextField
            select
            required
            label="User roles"
            variant="standard"
            error={!!roleError}
            helperText={roleError}
            value={selectedRoles}
            onChange={event => {
              const value = event.target.value;
              const values = typeof value === "string" ? value.split(",") : value;
              handleOnChangeRoles(event as unknown as React.SyntheticEvent<Element, Event>, values);
            }}
            slotProps={{
              select: {
                multiple: true,
                renderValue: selected => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {(selected as string[]).map(value => (
                      <Chip key={value} label={value} disabled={value === "Mobile User"} />
                    ))}
                  </Box>
                ),
              },
            }}
          >
            {roles.map(option => {
              const selected = selectedRoles.includes(option);
              return (
                <MenuItem key={option} value={option} disabled={option === "Mobile User"}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.toUpperCase()}
                </MenuItem>
              );
            })}
          </TextField>
          <TextField
            label={isEditing ? "Reset Password" : "Password"}
            id="password"
            value={password}
            error={!!passwordError}
            helperText={passwordError}
            required={!isEditing}
            onChange={handleOnPasswordChange}
            onBlur={handleOnPasswordChange}
            fullWidth
            margin="normal"
            type={showPassword ? "text" : "password"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => handleClickShowPassword()}
                    onMouseDown={event => event.preventDefault()}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClickSubmitButton}
          disabled={disableSaveButton}
          sx={theme => ({ width: 100, borderRadius: "20px", backgroundColor: alpha(theme.palette.primary.main, 0.5) })}
          disableElevation
          color="primary"
          variant="contained"
          type="button"
        >
          {isCustomCreateLoading && <CircularProgress size={16} />}
          Save
        </Button>
        <Button
          disabled={isCustomCreateLoading}
          disableElevation
          variant="outlined"
          sx={{ width: 100, borderRadius: "20px" }}
          onClick={handleClose}
          type="button"
        >
          Cancel
        </Button>
      </DialogActions>
    </Box>
  );
};
